<template>
  <main>
    <form class="container" @submit.prevent="submit">
      <al-notify v-if="errorToken" error name="error-msg">
        {{ $t(errorToken) }}
      </al-notify>

      <div class="row">
        <div class="col-full">
          <h1 class="heading-xl">
            {{ $t(heading) }}
          </h1>
        </div>
      </div>
      <div class="row">
        <div class="col-full">
          <al-card-preview
            v-if="card"
            :img="cardInfo.img"
            :brand="cardInfo.brand"
            :number="`•••• ${cardInfo.lastFour}`"
            name="card-preview" />
        </div>
      </div>

      <div class="row">
        <div class="col-full">
          <h2 class="heading-md">
            {{ $t('common.billingAddress') }}
          </h2>
        </div>
      </div>

      <tux-google-consent
        v-if="isAutocompleteApiEnabled"
        :privacy-url="googlePrivacyUrl"
        :terms-url="googleTermsUrl" />

      <tux-address-form
        v-model="billingAddress"
        :countries="countries"
        :enable-autocomplete="isAutocompleteApiEnabled"
        :enable-name-field="showNameField"
        class="margin-bottom-md"
        autocomplete="billing"
        name="billing-address"
        @autocompleteSelection="autocompleteSelected = true" />

      <div class="row row-vertical-align-bottom">
        <div class="col-full align-center">
          <button
            :disabled="hasValidationErrors"
            data-test="form-submit-button"
            class="fill uppercase"
            type="submit"
            :aria-label="$t(button.continue)">
            {{ $t(button.continue) }}
          </button>
        </div>
        <div class="col-full align-center">
          <back-button
            v-analytics:click="isManagingAddress ? $EVENTS.GO_BACK_BUTTON : $EVENTS.CANCEL_BUTTON"
            :location="cancelBtnLocation">
            {{ $t(button.cancel) }}
          </back-button>
        </div>
      </div>
    </form>
  </main>
</template>

<script>
import { AlCardPreview, AlNotify } from '@mc-alberta/alcapone'
import nudetect from '@mc-alberta/nudetect'
import { TuxAddressForm, TuxGoogleConsent } from '@mc-alberta/tuxedo'
import { getCurrentInstance } from '@vue/composition-api'
import { mapActions, mapGetters, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import useBindUserData from '@/composables/useBindUserData'
import useConfirmModal from '@/composables/useConfirmModal'
import useFormValidation from '@/composables/useFormValidation'
import constants from '@/constants'

export default {
  name: constants.ROUTES.ADD_BILLING,
  components: { AlCardPreview, AlNotify, BackButton, TuxGoogleConsent, TuxAddressForm },
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeaveConfirmModal(to, from, next)
  },
  setup(props, context) {
    const { proxy } = getCurrentInstance()

    const { errorToken, handleBind } = useBindUserData()
    const { beforeRouteLeaveConfirmModal } = useConfirmModal({
      context,
      forceConfirmationModal: true,
      onConfirmModalComplete: () => proxy.$store.dispatch('clearNewCardData')
    })
    const { hasValidationErrors } = useFormValidation()

    return {
      beforeRouteLeaveConfirmModal,
      errorToken,
      handleBind,
      hasValidationErrors
    }
  },
  data() {
    const { countryCode } = this.$store.state.user.consumer
    return {
      card: null,
      autocompleteSelected: false,
      billingAddress: { countryCode },
      unauthorizedFromLoadProfile: false,
      cancelBtnLocation: { name: constants.ROUTES.LIST_CARDS },
      storedPayload: null,
      isManagingAddress: false,
      googlePrivacyUrl: process.env.GOOGLE_PRIVACY,
      googleTermsUrl: process.env.GOOGLE_TERMS
    }
  },
  computed: {
    ...mapState({
      newCard: (state) => state.cards.newCard
    }),
    ...mapGetters(['countries', 'newCardAddressFormat', 'cardByCardId', 'serviceErrorResponse']),
    button() {
      return {
        continue: this.isManagingAddress ? 'button.save' : 'button.continue',
        cancel: this.isManagingAddress ? 'button.goBack' : 'button.cancel'
      }
    },
    emvcoCardBrand() {
      const { digitalCardData, paymentCardDescriptor } = this.card || {}
      const { presentationName, descriptorName } = digitalCardData || {}

      return presentationName || descriptorName || paymentCardDescriptor
    },
    nonEmvcoCardBrand() {
      return (
        this.card.shortProductDescription || this.card.issuerName || this.card.paymentNetworkName
      )
    },
    cardInfo() {
      return {
        brand: this.isManagingAddress ? this.nonEmvcoCardBrand : this.emvcoCardBrand,
        img: this.isManagingAddress ? this.card.artUrl : this.card?.digitalCardData.artUri,
        lastFour: this.isManagingAddress
          ? this.card.primaryAccountNumberSuffix
          : this.card?.panLastFour
      }
    },
    heading() {
      if (!this.isManagingAddress) return 'billingAddress.heading'
      return this.$route.params.hasBillingAddress
        ? 'billingAddress.editBilling'
        : 'billingAddress.addBilling'
    },
    isAutocompleteApiEnabled() {
      return (
        this.$switchboard.isCountryFeatureEnabled(
          constants.COUNTRY_FEATURES.ENABLE_ADDRESS_AUTOCOMPLETE
        ) && !this.$switchboard.isTestOverrideEnabled(constants.TEST_OVERRIDES.GOOGLE_AUTOCOMPLETE)
      )
    },
    showNameField() {
      return !this.isManagingAddress || !this.card.billingAddress?.fullName
    }
  },
  created() {
    this.isManagingAddress = !!this.$route.params.cardId

    if (!this.newCard && !this.isManagingAddress) {
      return this.$router.replace({
        name: constants.ROUTES.ADD_CARD,
        params: { skipConfirmation: true }
      })
    }

    if (this.isManagingAddress) {
      this.card = this.cardByCardId(this.$route.params.cardId)
      this.cancelBtnLocation = {
        name: constants.ROUTES.CARD_DETAILS,
        params: { cardId: this.card.id }
      }
    } else {
      // setting it here to avoid vue reactivity updating the value ~ Ronald
      this.card = { ...this.newCard }
    }
  },
  mounted() {
    this.$nextTick(() => nudetect.beginBehaviouralMonitoring())
    this.$validator.validateAll()
  },
  beforeDestroy() {
    this.clearServiceLayerError()
  },
  methods: {
    ...mapActions([
      'clearNewCardData',
      'clearServiceLayerError',
      'startLoader',
      'stopLoader',
      'updateBillingAddress'
    ]),

    async submit() {
      this.$analytics.track({
        event: this.isManagingAddress ? this.$EVENTS.SAVE_BUTTON : this.$EVENTS.CONTINUE_BUTTON,
        meta: {
          autocompleteSelected: this.autocompleteSelected,
          hasBillingData: constants.ADDRESS_FORMAT.NONE,
          hasShippingData: false,
          hasNationalId: false,
          marketBillingPreference: !this.isManagingAddress ? this.newCardAddressFormat : null,
          consumerNameRequested: false
        }
      })

      this.storedPayload = {
        billingAddress: this.billingAddress
      }

      this.startLoader(constants.LOADERS.UPDATE_PROFILE)

      if (this.isManagingAddress) {
        this.handleManageAddress()
      } else {
        await this.handleBind(this.storedPayload)
      }
    },
    async handleManageAddress() {
      await this.updateBillingAddress({ cardId: this.card.id, updatedAddress: this.billingAddress })
      if (this.serviceErrorResponse()) return this.handleError()

      this.$router.push({
        name: constants.ROUTES.CARD_DETAILS,
        params: {
          skipConfirmation: true,
          successToken: this.$route.params.hasBillingAddress
            ? 'cardDetails.editBillingSuccess'
            : 'cardDetails.addBillingSuccess',
          cardId: this.card.id
        }
      })

      this.stopLoader(constants.LOADERS.UPDATE_PROFILE)
    },
    handleError() {
      const { status, data } = this.serviceErrorResponse()
      const isSourceBilling = data.details?.some(({ source }) => {
        return source === constants.ERROR_SOURCE.BILLING_ADDRESS
      })

      if (status === 400 && isSourceBilling) {
        nudetect.beginBehaviouralMonitoring()
        this.errorToken = 'error.billing'
      } else {
        this.$router.replace({ name: constants.ROUTES.ERROR })
      }

      this.stopLoader(constants.LOADERS.UPDATE_PROFILE)
    }
  }
}
</script>
